<script setup lang="ts">
import type { IconKeys } from "@/components/ui/Icon/Icon.interface"
import type { IAlertVariant } from "@/types"
import { onMounted, watch } from "vue"
import { Icon } from "@/components/ui"
import { useTranslation } from "@/composables/useTranslation"

interface AlertProps {
  variant?: IAlertVariant
  prefixIcon?: IconKeys
  closable?: boolean
  sharpCorners?: boolean
  compact?: boolean
  modelValue?: string
  dismissAfter?: number
}

const props = withDefaults(defineProps<AlertProps>(), {
  variant: "primary",
  closable: true,
  sharpCorners: false,
  compact: false,
  modelValue: "",
})

const emit = defineEmits<{
  "update:modelValue": [value: string]
  "dismiss": []
}>()

const { t } = useTranslation()

function clear() {
  emit("update:modelValue", "")
  emit("dismiss")
}

onMounted(() => {
  if (props.dismissAfter && props.dismissAfter > 0) {
    setTimeout(clear, props.dismissAfter)
  }
})

watch(
  () => props.modelValue,
  () => {
    if (props.dismissAfter && props.dismissAfter > 0) {
      setTimeout(clear, props.dismissAfter)
    }
  },
)
</script>

<template>
  <Transition name="gpx-fade">
    <div
      v-if="modelValue || $slots.default"
      role="alert"
      class="text-base flex gap-2 items-start mb-0 p-2 border"
      :class="{
        'bg-primary/10 text-primary border-primary': variant === 'primary',
        'bg-primary text-primary-card-text border-primary': variant === 'primary-solid',
        'bg-[#072a47]/10 text-[#072a47] border-[#072a47]': variant === 'info',
        'bg-theme-success/10 border-theme-success text-theme-success': variant === 'success',
        'bg-theme-danger/10 border-theme-danger text-theme-danger': variant === 'danger',
        'rounded-lg': !sharpCorners,
        'p-4': !compact,
      }"
    >
      <div class="flex justify-center items-center gap-2 h-full w-full">
        <slot name="prefixIcon">
          <Icon v-if="prefixIcon" :icon="prefixIcon" />
        </slot>
        <div class="w-full font-montserrat font-medium text-sm">
          <slot>{{ modelValue }}</slot>
        </div>
      </div>
      <button v-if="closable" class="ml-5" @click="clear">
        <span class="sr-only">{{ t("alert.dismiss") }}</span>
        <Icon icon="Close" size="sm" />
      </button>
    </div>
  </transition>
</template>
